import { Autocomplete, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { memo, useCallback, useState } from "react";
import type { ExternalReviewer } from "../../api/assignments/external-review";

// ---------------------------------------------------------------------------
// Reviewer form — owns its own text-input state so keystrokes don't
// re-render the parent (which holds the 400-item person list).
// ---------------------------------------------------------------------------

export interface ReviewerRef {
	reviewer: ExternalReviewer | null;
	isAddingNew: boolean;
	newName: string;
	newEmail: string;
}

export const ReviewerSection = memo(
	({
		reviewers,
		onReviewerChange,
	}: {
		reviewers: ExternalReviewer[];
		onReviewerChange: (ref: ReviewerRef) => void;
	}) => {
		const [selectedReviewer, setSelectedReviewer] =
			useState<ExternalReviewer | null>(null);
		const [isAddingNew, setIsAddingNew] = useState(false);
		const [newName, setNewName] = useState("");
		const [newEmail, setNewEmail] = useState("");

		const notify = useCallback(
			(patch: Partial<ReviewerRef>) => {
				const next = {
					reviewer: patch.reviewer ?? selectedReviewer,
					isAddingNew: patch.isAddingNew ?? isAddingNew,
					newName: patch.newName ?? newName,
					newEmail: patch.newEmail ?? newEmail,
				};
				onReviewerChange(next);
			},
			[selectedReviewer, isAddingNew, newName, newEmail, onReviewerChange]
		);

		return (
			<>
				<Typography
					variant="subtitle2"
					color="text.secondary"
					sx={{ mb: 1, mt: 1 }}
				>
					Reviewer
				</Typography>

				{!isAddingNew ? (
					<Stack spacing={1}>
						<Autocomplete
							data-component="reviewer-autocomplete"
							options={reviewers}
							getOptionLabel={(r) => `${r.name} (${r.email})`}
							value={selectedReviewer}
							onChange={(_e, value) => {
								setSelectedReviewer(value);
								notify({ reviewer: value });
							}}
							forcePopupIcon={!selectedReviewer}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select reviewer"
									size="small"
									type="search"
									sx={{
										"& input[type=search]::-webkit-search-cancel-button":
											{
												display: "none",
											},
									}}
								/>
							)}
						/>
						<Button
							data-component="add-new-reviewer-button"
							size="small"
							onClick={() => {
								setIsAddingNew(true);
								notify({ isAddingNew: true });
							}}
						>
							+ Add new reviewer
						</Button>
					</Stack>
				) : (
					<Box
						component="form"
						role="search"
						noValidate
						data-component="new-reviewer-form"
					>
						<Stack spacing={1}>
							<TextField
								data-component="new-reviewer-name"
								label="Name"
								size="small"
								fullWidth
								value={newName}
								onChange={(e) => {
									setNewName(e.target.value);
									notify({ newName: e.target.value });
								}}
							/>
							<TextField
								data-component="new-reviewer-email"
								label="Mail"
								size="small"
								fullWidth
								value={newEmail}
								onChange={(e) => {
									setNewEmail(e.target.value);
									notify({ newEmail: e.target.value });
								}}
							/>
							<Button
								data-component="pick-existing-button"
								size="small"
								onClick={() => {
									setIsAddingNew(false);
									notify({ isAddingNew: false });
								}}
							>
								Pick existing reviewer
							</Button>
						</Stack>
					</Box>
				)}
			</>
		);
	}
);
